import { AmplifyProvider, Authenticator, Theme, useTheme } from "@aws-amplify/ui-react";
import { useAuthenticator } from "@aws-amplify/ui-react";

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Box, Stack } from "@mui/material";

import useNavigateLogin from "@/hooks/useNavigateLogin";
import useThemeData from "@/hooks/useThemeData";

import Content from "../SignIn/components/Content";

function Auth() {
  const navigate = useNavigate();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { getRedirectUrl } = useNavigateLogin();

  useEffect(() => {
    if (authStatus === "authenticated") {
      location.href = getRedirectUrl() || "/";

      localStorage.setItem("isLogin", "true");
    }
  }, [authStatus, getRedirectUrl, navigate]);

  return (
    <Box className="grid w-screen transition-all place-items-center">
      <Stack
        direction={{ xs: "column-reverse", md: "column-reverse", lg: "row" }}
        sx={{
          justifyContent: "center",
          gap: { xs: 6, sm: 5 },
          p: { xs: 2, sm: 10 },
          m: "auto",
        }}
      >
        <Content />
        <Authenticator />
      </Stack>
    </Box>
  );
}

const SignInPassword = () => {
  const { tokens } = useTheme();

  const muiTheme = useThemeData();
  const theme: Theme = {
    name: "material-style-theme",
    overrides: [
      {
        colorMode: "dark",
        tokens: {
          components: {
            authenticator: {
              router: {
                backgroundColor: muiTheme.palette.background.paper,
                boxShadow: `0 0 16px ${tokens.colors.overlay["10"]}`,
                borderWidth: "0",
              },
              form: {
                padding: `${tokens.space.medium} ${tokens.space.xl} ${tokens.space.medium}`,
              },
            },
            button: {
              color: "#fff",
              _hover: {
                backgroundColor: "rgba(144, 202, 249, 0.08)",
              },
              primary: {
                backgroundColor: muiTheme.palette.primary.main,
                color: "#000",
                _hover: {
                  backgroundColor: muiTheme.palette.primary.dark,
                  color: "#000",
                },
              },
              link: {
                color: muiTheme.palette.primary.main,
                _hover: {
                  backgroundColor: "transparent",
                  color: muiTheme.palette.primary.light,
                },
              },
            },
            text: {
              color: "#fff",
              error: {
                color: muiTheme.palette.primary.main,
              },
            },
            field: {
              label: {
                color: "#fff",
              },
            },
            fieldcontrol: {
              color: "#fff",
              _focus: {
                boxShadow: `0 0 0 1px ${muiTheme.palette.primary.main}`,
              },
              _error: {
                color: "#fff",
                borderColor: muiTheme.palette.primary.main,
              },
            },
            tabs: {
              item: {
                color: "#fff",
                _active: {
                  color: muiTheme.palette.primary.main,
                },
                _focus: {
                  color: muiTheme.palette.primary.main,
                },
                _hover: {
                  color: muiTheme.palette.primary.main,
                },
              },
            },
            divider: {
              label: {
                color: "#fff",
                backgroundColor: "#2D2D2D",
              },
            },
          },
        },
      },
      {
        colorMode: "light",
        tokens: {
          components: {
            authenticator: {
              router: {
                boxShadow: `0 0 16px ${tokens.colors.overlay["10"]}`,
                borderWidth: "0",
              },
              form: {
                padding: `${tokens.space.medium} ${tokens.space.xl} ${tokens.space.medium}`,
              },
            },
            button: {
              primary: {
                backgroundColor: muiTheme.palette.primary.main,
                _hover: {
                  backgroundColor: muiTheme.palette.primary.dark,
                },
              },
              link: {
                color: muiTheme.palette.primary.main,
              },
            },
            fieldcontrol: {
              _focus: {
                boxShadow: `0 0 0 1px ${muiTheme.palette.primary.main}`,
              },
            },
            tabs: {
              item: {
                color: tokens.colors.neutral["80"],
                _active: {
                  color: muiTheme.palette.primary.main,
                },
                _focus: {
                  color: muiTheme.palette.primary.main,
                },
                _hover: {
                  color: muiTheme.palette.primary.main,
                },
              },
            },
          },
        },
      },
    ],
  };
  return (
    <AmplifyProvider theme={theme} colorMode={muiTheme.isDarkMode ? "dark" : "light"}>
      <Authenticator.Provider>
        <Auth />
      </Authenticator.Provider>
    </AmplifyProvider>
  );
};

export default SignInPassword;
